import { useParams, Link } from 'react-router-dom';
import { useEffect } from 'react';
import Navigation from '../../components/feature/Navigation';
import SEOHead from '../../components/feature/SEOHead';
import ProjectHero from './components/ProjectHero';
import ProjectContent from './components/ProjectContent';
import { getProjectBySlug } from '../../mocks/projects';

export default function ProjectPage() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const project = slug ? getProjectBySlug(slug) : undefined;

  const projectSchema = project
    ? {
        '@context': 'https://schema.org',
        '@type': 'ExhibitionEvent',
        '@id': `https://oludamolaadebowale.com/project/${project.slug}`,
        name: project.title,
        description: project.description,
        organizer: { '@id': 'https://oludamolaadebowale.com/#person' },
        startDate: project.year?.split(' – ')[0] ?? '',
        location: project.venue
          ? { '@type': 'Place', name: project.venue + ', Nigeria' }
          : { '@type': 'Place', name: 'Lagos, Nigeria' },
        image: project.image,
        url: `https://oludamolaadebowale.com/project/${project.slug}`,
        breadcrumb: {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://oludamolaadebowale.com/' },
            { '@type': 'ListItem', position: 2, name: 'Projects', item: 'https://oludamolaadebowale.com/#research' },
            { '@type': 'ListItem', position: 3, name: project.title, item: `https://oludamolaadebowale.com/project/${project.slug}` },
          ],
        },
      }
    : undefined;

  if (!project) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-stone-50">
        <Navigation />
        <div className="text-center mt-32 px-6">
          <p className="font-sans text-xs uppercase tracking-widest text-gray-400 mb-4">404</p>
          <h1 className="font-serif text-4xl font-bold text-gray-900 mb-6">Project Not Found</h1>
          <p className="font-sans text-base text-gray-500 mb-10">
            This project page doesn&apos;t exist yet or may have moved.
          </p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 font-sans text-sm bg-gray-900 text-white px-6 py-3 rounded-md hover:bg-gray-700 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-left-line" />
            Back to Home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title={`${project.title} | Oludamola Adébòwálè`}
        description={project.description.slice(0, 155) + (project.description.length > 155 ? '…' : '')}
        keywords={`${project.title}, Oludamola Adebowale, Nigerian cultural heritage, ${project.category.toLowerCase()}, Lagos Nigeria`}
        canonical={`https://oludamolaadebowale.com/project/${project.slug}`}
        ogType="article"
        ogImage={project.image}
        schemaJson={projectSchema}
      />
      <Navigation />
      <div className="pt-20">
        <ProjectHero project={project} />
        <ProjectContent project={project} />
      </div>
    </div>
  );
}
